import React, { Suspense, useEffect, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './utils/auth.jsx';
import { LanguageProvider } from './i18n/context/LanguageContext';
import { QRStatusProvider } from './contexts/QRStatusContext';
import GoogleAnalytics from './components/analytics/GoogleAnalytics.jsx';
import DebugModeToggle from './components/DebugModeToggle.jsx';

// 코드 스플리팅: 페이지별 lazy loading
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignUpPage = lazy(() => import('./pages/SignUpPage'));
const CaricatureCreate = lazy(() => import('./pages/CaricatureCreate'));
const CaricatureResult = lazy(() => import('./pages/CaricatureResult'));
const CaricatureResultToken = lazy(() => import('./pages/CaricatureResultToken'));
const CaricatureResultDirect = lazy(() => import('./pages/CaricatureResultDirect'));
const QRCodeGenerator = lazy(() => import('./pages/QRCodeGenerator'));
const QRCodeEdit = lazy(() => import('./pages/QRCodeEdit'));
const PrintTrackPage = lazy(() => import('./pages/PrintTrackPage'));
const SharedStylePage = lazy(() => import('./pages/SharedStylePage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const AIHistory = lazy(() => import('./pages/AIHistory'));
const AITestPage = lazy(() => import('./pages/AITestPage'));
const QRIssueKiosk = lazy(() => import('./pages/QRIssueKiosk'));
const ManagerQRIssue = lazy(() => import('./pages/ManagerQRIssue'));
const PresetManager = lazy(() => import('./pages/PresetManager'));
const PointManagement = lazy(() => import('./pages/PointManagement'));
const SharePage = lazy(() => import('./pages/SharePage'));
const ShareLandingPage = lazy(() => import('./pages/ShareLandingPage'));
const AlbumPage = lazy(() => import('./pages/AlbumPage'));
const MyAlbumsPage = lazy(() => import('./pages/MyAlbumsPage'));
const AlbumJoinPage = lazy(() => import('./pages/AlbumJoinPage'));
const AlbumDashboard = lazy(() => import('./pages/AlbumDashboard'));
const InsightsPage = lazy(() => import('./pages/InsightsPage'));
const PaymentDetailPage = lazy(() => import('./pages/PaymentDetailPage'));
const StoreWizard = lazy(() => import('./pages/StoreWizard'));
const TabClosedPage = lazy(() => import('./pages/TabClosedPage'));
const LocationManagement = lazy(() => import('./pages/LocationManagement'));

import { debugLog, debugWarn, debugError } from './utils/debug.js';
import { registerTab } from './utils/tabManager.js';
function PrivateRoute({ children, allowIframe = false }) {
  const { user, loading } = useAuth();

  // iframe 내부인지 확인
  const isInIframe = window.self !== window.top;

  // iframe이고 허용된 경우 인증 체크 건너뛰기
  if (isInIframe && allowIframe) {
    return children;
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>로딩 중...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>로딩 중...</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/signup" element={user ? <Navigate to="/" replace /> : <SignUpPage />} />
      {/* 메인 페이지 - 파라미터 없으면 /preset으로 리다이렉트 */}
      <Route path="/" element={<CaricatureCreate />} />
      {/* 일회용 토큰 기반 결과 페이지 (새로고침 방지) */}
      <Route path="/result/:token" element={<CaricatureResultToken />} />
      {/* QR 코드로 이미 생성된 이미지 직접 보기 */}
      <Route path="/result-direct" element={<CaricatureResultDirect />} />
      {/* 구형 결과 페이지 (호환성 유지) */}
      <Route path="/old-result/:jobId" element={<CaricatureResult />} />
      {/* QR 발급 키오스크 (로그인 없이 접근 가능) */}
      <Route path="/kiosk" element={<QRIssueKiosk />} />
      {/* 묶음 인쇄 페이지/설정은 fitting 프로젝트(fit.wizice.com)로 이전:
          /caricature/bundle/:albumId (촬영), /caricature/bundle-settings/:albumId (설정).
          q 에는 bundle-print/bundle-config 엔드포인트만 유지. */}
      {/* 관리자 QR 발급 (로그인 필요) */}
      <Route
        path="/manager/qr-issue"
        element={
          <PrivateRoute>
            <ManagerQRIssue />
          </PrivateRoute>
        }
      />
      <Route
        path="/qr"
        element={
          <PrivateRoute>
            <QRCodeGenerator />
          </PrivateRoute>
        }
      />
      {/* QR 설정 수정 - 관리자 비밀번호로 접근 (로그인 불필요) */}
      <Route path="/qr-edit" element={<QRCodeEdit />} />
      <Route
        path="/print-track/:jobId"
        element={
          <PrivateRoute allowIframe={true}>
            <PrintTrackPage />
          </PrivateRoute>
        }
      />
      {/* AI 이미지 공유 페이지 - 로그인 없이 접근 가능 */}
      <Route path="/share" element={<SharePage />} />
      {/* 스타일 공유 페이지 - 로그인 없이 접근 가능 */}
      <Route path="/share/:slug" element={<SharedStylePage />} />
      <Route
        path="/analytics"
        element={
          <PrivateRoute>
            <AnalyticsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/ai-history"
        element={
          <PrivateRoute>
            <AIHistory />
          </PrivateRoute>
        }
      />
      <Route
        path="/ai-test"
        element={
          <PrivateRoute>
            <AITestPage />
          </PrivateRoute>
        }
      />
      {/* QR 프리셋 관리 페이지 - 로그인 없이도 접근 가능 */}
      <Route path="/preset" element={<PresetManager />} />
      {/* 포인트 관리 페이지 - 로그인 필요 */}
      <Route
        path="/point"
        element={
          <PrivateRoute>
            <PointManagement />
          </PrivateRoute>
        }
      />
      {/* 인사이트 대시보드 (super-admin 전용) */}
      <Route
        path="/insights"
        element={
          <PrivateRoute>
            <InsightsPage />
          </PrivateRoute>
        }
      />
      {/* 매장 매니저 위저드 */}
      <Route
        path="/store-wizard"
        element={
          <PrivateRoute>
            <StoreWizard />
          </PrivateRoute>
        }
      />
      {/* 앨범 페이지 */}
      <Route path="/albums" element={<MyAlbumsPage />} />
      <Route path="/album" element={<AlbumPage />} />
      <Route path="/album/join/:code" element={<AlbumJoinPage />} />
      <Route path="/album/dashboard" element={<AlbumDashboard />} />
      {/* Location 관리 (super-admin only) */}
      <Route
        path="/admin/locations"
        element={
          <PrivateRoute>
            <LocationManagement />
          </PrivateRoute>
        }
      />
      {/* 결제 상세 페이지 (토큰 기반, 로그인 불필요) */}
      <Route path="/admin/payment/:token" element={<PaymentDetailPage />} />
      {/* 탭 자동 닫기 안내 페이지 (오래된 탭이 교체되는 곳) */}
      <Route path="/tab-closed" element={<TabClosedPage />} />
      {/* QR 공유 랜딩 페이지 - /:qrCode 보다 앞에 배치 */}
      <Route path="/v/:shareId/:flowSessionId" element={<ShareLandingPage />} />
      <Route path="/v/:shareId" element={<ShareLandingPage />} />
      {/* QR 코드 경로 (/:qrCode) - 맨 마지막에 배치하여 다른 라우트와 충돌 방지 */}
      <Route path="/:qrCode" element={<CaricatureCreate />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // 탭 자동 닫기 (동일 도메인 3개 초과 시 가장 오래된 탭 교체)
  useEffect(() => {
    const unregister = registerTab({
      channelName: 'smileprint-tabs',
      storageKey: 'smileprint_open_tabs',
      maxTabs: 3,
      closedUrl: '/tab-closed?reason=auto',
    });
    return unregister;
  }, []);

  // 최초 로드 시 고객 ID 확인 및 생성
  useEffect(() => {
    const initializeCustomer = async () => {
      try {
        // ⭐ 서버 세션에서 customer_id 확인 (localStorage fallback 포함)
        const { getCustomerId, setCustomerId } = await import('./utils/customerSession.js');
        let customerId = await getCustomerId();

        if (!customerId) {
          debugLog('[Customer] 📝 No customer_id found, creating new customer...');

          // QR 코드 정보가 있으면 함께 저장
          const qrInfoStr = sessionStorage.getItem('qr_info');
          let qrcode = null;
          if (qrInfoStr) {
            try {
              const qrInfo = JSON.parse(qrInfoStr);
              qrcode = qrInfo.qrCode || qrInfo.qr_code_id || qrInfo.qr_code;
            } catch (err) {
              debugWarn('[Customer] Failed to parse qr_info:', err);
            }
          }

          // 신규 고객 등록
          const response = await fetch('/api/customer/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ qrcode })
          });

          const data = await response.json();

          if (data.success) {
            customerId = data.data.customer_id;
            // ⭐ 서버 세션 + localStorage 모두에 저장
            await setCustomerId(customerId);
            debugLog('[Customer] ✅ Customer registered:', customerId);
          } else {
            debugError('[Customer] ❌ Failed to register customer:', data.error);
          }
        } else {
          debugLog('[Customer] ✅ Existing customer_id found:', customerId);
        }
      } catch (error) {
        debugError('[Customer] ❌ Error initializing customer:', error);
      }
    };

    initializeCustomer();
  }, []);

  return (
    <BrowserRouter>
      <GoogleAnalytics />
      <DebugModeToggle />
      <AuthProvider>
        <LanguageProvider>
          {/* QR 상태 실시간 업데이트 (SSE) - 선택적 기능, 실패해도 앱 정상 작동 */}
          <QRStatusProvider>
            <Suspense fallback={<div className="loading"><div className="spinner"></div><p>로딩 중...</p></div>}>
              <AppContent />
            </Suspense>
          </QRStatusProvider>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
